import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Badge, Spinner } from 'react-bootstrap';
import { IconArrowLeft, IconPaperclip, IconSend, IconWifi, IconWifiOff } from '@tabler/icons-react';
import { toast } from 'react-toastify';
import { api } from '../../shared/api';
import { useSupportSocket } from '@shared/useSupportSocket';
import { useAuth } from '@shared/AuthContext';
import StatusBadge from '../../components/StatusBadge';
import './SupportTicketDetail.css';

/** Download an attachment through the API with the Bearer token. */
function downloadAttachment(attId, filename) {
  api.get(`/support/api/attachments/${attId}/download/`, { responseType: 'blob' })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    })
    .catch(() => toast.error('Download failed.'));
}

const STATUS_VARIANT = {
  open:           'danger',
  pending:        'warning',
  awaiting_reply: 'info',
  resolved:       'success',
  closed:         'secondary',
};

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_FILES     = 5;
const ALLOWED_EXTS  = ['.pdf','.png','.jpg','.jpeg','.gif','.webp','.zip','.csv','.xlsx','.docx'];

export default function SupportTicketDetail() {
  const { reference } = useParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [replyBody, setReplyBody]   = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [fileError, setFileError]   = useState('');
  const [sending, setSending]       = useState(false);
  const [liveMessages, setLiveMessages] = useState([]);

  const bottomRef = useRef(null);

  // Fetch ticket
  const { data: ticket, isLoading: ticketLoading } = useQuery({
    queryKey: ['support', 'ticket', reference],
    queryFn: () => api.get(`support/api/tickets/`).then(
      (list) => list.find((t) => t.reference === reference)
    ),
    enabled: !!reference,
  });

  // Fetch messages (initial load)
  const { data: messages = [], isLoading: msgsLoading } = useQuery({
    queryKey: ['support', 'messages', reference],
    queryFn: () =>
      ticket
        ? api.get(`support/api/tickets/${ticket.id}/messages/`)
        : Promise.resolve([]),
    enabled: !!ticket?.id,
  });

  // WebSocket — append live messages without re-fetching
  const handleWsEvent = useCallback((event) => {
    if (event.type === 'message') {
      setLiveMessages((prev) => {
        // Deduplicate by id
        if (prev.some((m) => m.id === event.id)) return prev;
        return [...prev, event];
      });
    }
    if (event.type === 'status_changed') {
      queryClient.invalidateQueries({ queryKey: ['support', 'ticket', reference] });
    }
  }, [reference, queryClient]);

  const { connected } = useSupportSocket(reference, handleWsEvent);

  // Scroll to bottom when messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, liveMessages]);

  // Combine server-loaded + live WebSocket messages, dedup by id
  const allMessages = React.useMemo(() => {
    const seen = new Set();
    return [...messages, ...liveMessages].filter((m) => {
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    });
  }, [messages, liveMessages]);

  function handleFiles(e) {
    const selected = Array.from(e.target.files);
    if (selected.length > MAX_FILES) { setFileError(`Max ${MAX_FILES} files.`); return; }
    for (const f of selected) {
      if (f.size > MAX_FILE_SIZE) { setFileError(`"${f.name}" exceeds 10 MB.`); return; }
      const ext = f.name.toLowerCase().slice(f.name.lastIndexOf('.'));
      if (!ALLOWED_EXTS.includes(ext)) { setFileError(`File type "${ext}" not allowed.`); return; }
    }
    setFileError('');
    setReplyFiles(selected);
  }

  async function handleReply(e) {
    e.preventDefault();
    const body = replyBody.trim();
    if (!body || !ticket) return;
    if (fileError) return;

    setSending(true);
    try {
      const message = await api.post(`support/api/tickets/${ticket.id}/messages/`, { body_html: body });

      // Upload attachments to the new message
      if (replyFiles.length > 0) {
        const fd = new FormData();
        replyFiles.forEach((f) => fd.append('files', f));
        try {
          await api.post(
            `support/api/tickets/${ticket.id}/messages/${message.id}/upload/`,
            fd
          );
        } catch {
          toast.warn(t('support.uploadWarning', 'Reply sent but attachment upload failed.'));
        }
      }

      setReplyBody('');
      setReplyFiles([]);
      // Invalidate to re-fetch with attachments
      queryClient.invalidateQueries({ queryKey: ['support', 'messages', reference] });
    } catch {
      toast.error(t('support.replyError', 'Failed to send reply.'));
    } finally {
      setSending(false);
    }
  }

  if (ticketLoading) {
    return (
      <div className="page-body d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="page-body">
        <div className="container-xl">
          <div className="alert alert-danger">{t('support.ticketNotFound', 'Ticket not found.')}</div>
          <Link to="/support" className="btn btn-outline-secondary mt-2">
            <IconArrowLeft size={16} className="me-1" />
            {t('support.backToList', 'Back to tickets')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-body">
      <div className="container-xl">

        {/* Header */}
        <div className="page-header d-print-none">
          <div className="row align-items-center">
            <div className="col">
              <Link to="/support" className="btn btn-sm btn-outline-secondary mb-2">
                <IconArrowLeft size={14} className="me-1" />
                {t('support.backToList', 'Tickets')}
              </Link>
              <h2 className="page-title mb-1">{ticket.title}</h2>
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <span className="text-muted small">#{ticket.reference}</span>
                <Badge bg={STATUS_VARIANT[ticket.status] || 'secondary'} className="text-capitalize">
                  {t(`support.status.${ticket.status}`, ticket.status)}
                </Badge>
                <Badge bg="outline-secondary" className="text-capitalize text-muted border">
                  {t(`support.priority.${ticket.priority}`, ticket.priority)}
                </Badge>
                {ticket.category && (
                  <StatusBadge color={ticket.category.color} label={ticket.category.name} />
                )}
                <span className="ms-auto">
                  {connected
                    ? <span className="text-success small"><IconWifi size={14} className="me-1" />{t('support.live', 'Live')}</span>
                    : <span className="text-muted small"><IconWifiOff size={14} className="me-1" />{t('support.offline', 'Reconnecting…')}</span>
                  }
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Original body */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="support-message support-message--user">
              <div className="support-message__meta">
                <strong>{ticket.author_name}</strong>
                <span className="text-muted small ms-2">
                  {new Date(ticket.created_at).toLocaleString()}
                </span>
              </div>
              <div
                className="support-message__body mt-2"
                dangerouslySetInnerHTML={{ __html: ticket.body_html }}
              />
            </div>
          </div>
        </div>

        {/* Message thread */}
        {msgsLoading && (
          <div className="text-center text-muted py-3">
            <Spinner animation="border" size="sm" className="me-2" />
            {t('common.loading', 'Loading…')}
          </div>
        )}

        {allMessages.map((msg) => (
          <div
            key={msg.id}
            className={`card mb-2 support-message-card ${msg.is_staff_reply ? 'support-message-card--staff' : ''}`}
          >
            <div className="card-body py-3">
              <div className="support-message__meta d-flex gap-2 align-items-center mb-1 flex-wrap">
                <strong>{msg.author_name}</strong>
                {msg.is_staff_reply && (
                  <Badge bg="primary" className="small">{t('support.staffBadge', 'Support')}</Badge>
                )}
                <span className="text-muted small ms-auto">
                  {new Date(msg.created_at).toLocaleString()}
                </span>
              </div>
              <div
                className="support-message__body"
                dangerouslySetInnerHTML={{ __html: msg.body_html }}
              />
              {msg.attachments?.length > 0 && (
                <div className="mt-2 d-flex gap-2 flex-wrap">
                  {msg.attachments.map((att) => (
                    <button
                      key={att.id}
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => downloadAttachment(att.id, att.original_filename)}
                    >
                      <IconPaperclip size={14} className="me-1" />
                      {att.original_filename}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        <div ref={bottomRef} />

        {/* Reply box (hidden if ticket is closed/resolved) */}
        {!['closed', 'resolved'].includes(ticket.status) && (
          <div className="card mt-3">
            <div className="card-body">
              <h5 className="card-title mb-3">{t('support.replyTitle', 'Add a reply')}</h5>
              <form onSubmit={handleReply}>
                <textarea
                  className="form-control mb-3"
                  rows={4}
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  placeholder={t('support.replyPlaceholder', 'Write your reply…')}
                  required
                />

                <div className="mb-3">
                  <label className="form-label small text-muted d-flex align-items-center gap-1" htmlFor="reply-files">
                    <IconPaperclip size={14} />
                    {t('support.attachFiles', 'Attach files')}
                    <span className="ms-1 small">{t('support.fields.attachmentsHint', '(max 5, 10 MB each)')}</span>
                  </label>
                  <input
                    id="reply-files"
                    type="file"
                    className={`form-control form-control-sm ${fileError ? 'is-invalid' : ''}`}
                    multiple
                    accept={ALLOWED_EXTS.join(',')}
                    onChange={handleFiles}
                  />
                  {fileError && <div className="invalid-feedback">{fileError}</div>}
                </div>

                <div className="d-flex justify-content-end">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={sending || !replyBody.trim() || !!fileError}
                  >
                    {sending ? (
                      <><Spinner size="sm" animation="border" className="me-2" />{t('common.sending', 'Sending…')}</>
                    ) : (
                      <><IconSend size={16} className="me-1" />{t('support.sendReply', 'Send reply')}</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
